import { useEffect, useState } from "react";
import { FaStar } from "react-icons/fa";
import { IoEyeOutline } from "react-icons/io5";

interface ColumnConfig {
  key: string;
  label: string;
  width?: string;
  align?: "left" | "center" | "right";
  render?: (row: Record<string, any>) => React.ReactNode; // ✅ custom render
}
interface StylesConfig {
  headerBg: string;
  headerText: string;
  rowHover: string;
  statusStyles?: Record<string, { background: string; textColor: string }>;
  keywordStyles?: Record<string, { background: string; textColor: string }>;
}

export interface ProductListConfig {
  columns: ColumnConfig[];
  styles: StylesConfig;
}

interface TableProps {
  data: Record<string, any>[];
  config: ProductListConfig;
  height?: string;
  rowsPerPage?: number;
}

export default function DataTable({
  data,
  config,
  height = "max-h-[600px]",
  rowsPerPage = 10,
}: TableProps) {
  // Safety check for config
  if (!config) {
    console.error("DataTable: config prop is required");
    return <div className="p-4 text-red-500">Error: Table configuration is missing</div>;
  }

  // Safety check for required config properties
  if (!config.columns || !config.styles) {
    console.error("DataTable: config must have columns and styles properties", config);
    return <div className="p-4 text-red-500">Error: Invalid table configuration</div>;
  }

  const { columns, styles } = config;

  // Pagination State
  const [currentPage, setCurrentPage] = useState(1);
  const hasData = data.length > 0;
  const totalPages = Math.max(1, Math.ceil(data.length / rowsPerPage));

  useEffect(() => {
    // Keep page index valid when filters or dataset size changes.
    setCurrentPage((prev) => Math.min(Math.max(prev, 1), totalPages));
  }, [totalPages]);

  const handlePrev = () => setCurrentPage((p) => Math.max(p - 1, 1));
  const handleNext = () => setCurrentPage((p) => Math.min(p + 1, totalPages));

  // Paginated Data
  const paginatedData = data.slice(
    (currentPage - 1) * rowsPerPage,
    currentPage * rowsPerPage
  );

  return (
    <div className="overflow-x-auto bg-white rounded-lg shadow border border-gray-200">
      {/* Table Scroll Container */}
      <div className={`${height} overflow-y-auto`}>
        <table className="min-w-full text-left border-collapse">
          {/* Table Header */}
          <thead className={`${styles.headerBg} border-b sticky top-0 z-10`}>
            <tr>
              {columns.map((col: ColumnConfig) => (
                <th
                  key={col.key}
                  className={`${col.width || ""} px-4 py-3 font-medium ${styles.headerText} text-sm text-${col.align || "left"}`}
                >
                  {col.label}
                </th>
              ))}
            </tr>
          </thead>

          {/* Table Body */}
          <tbody>
            {paginatedData.length ? (
              paginatedData.map((row, idx) => (
                <tr key={idx} className={`border-b ${styles.rowHover}`}>
                  {columns.map((col: ColumnConfig) => {
                    const value = row[col.key];

                    // Status pill
                    if (col.key === "status") {
                      const style = styles.statusStyles?.[value];
                      return (
                        <td
                          key={col.key}
                          className={`${col.width || ""} px-4 py-3 text-sm text-${
                            col.align || "left"
                          }`}
                        >
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              style?.background || ""
                            } ${style?.textColor || "text-gray-700"}`}
                          >
                            {value}
                          </span>
                        </td>
                      );
                    }
                    if (col.render) {
                      return (
                        <td
                          key={col.key}
                          className={`${col.width || ""} px-4 py-3 text-sm text-${col.align || "left"}`}
                        >
                          {col.render(row)}
                        </td>
                      );
                    }

                    if (col.key === "keywords") {
                      const style = styles.keywordStyles?.[value];
                      return (
                        <td
                          key={col.key}
                          className={`${col.width || ""} px-4 py-3 text-sm text-${
                            col.align || "center"
                          }`}
                        >
                          <span
                            className={`px-2 py-1 rounded-full text-xs font-medium ${
                              style?.background || "bg-gray-100"
                            } ${style?.textColor || "text-gray-700"}`}
                          >
                            {value}
                          </span>
                        </td>
                      );
                    }
                    if (col.key === "score") {
                      return (
                        <td
                          key={col.key}
                          className={`px-4 py-3 text-sm text-${col.align || "center"}`}
                        >
                          <span className="inline-flex items-center gap-1">
                            <FaStar className="w-4 h-4 text-orange-500 " />
                            {value}
                          </span>
                        </td>
                      );
                    }

                    if (col.key === "visitors") {
                      return (
                        <td
                          key={col.key}
                          className={`px-4 py-3 text-sm text-${col.align || "center"}`}
                        >
                          <span className="inline-flex items-center gap-1">
                            <IoEyeOutline className="w-4 h-4 text-gray-500" />
                            {value}
                          </span>
                        </td>
                      );
                    }

                    return (
                      <td
                        key={col.key}
                        className={`${col.width || ""} px-4 py-3 text-sm text-gray-600 text-${
                          col.align || "left"
                        }`}
                      >
                        {value}
                      </td>
                    );
                  })}
                </tr>
              ))
            ) : (
              <tr>
                <td
                  colSpan={columns.length}
                  className="text-center py-4 text-gray-500"
                >
                  No data found
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Pagination Controls */}
      <div className="flex items-center justify-between p-4 border-t">
        <button
          onClick={handlePrev}
          disabled={!hasData || currentPage === 1}
          className={`px-3 py-1 rounded ${
            !hasData || currentPage === 1
              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
              : "bg-blue-500 text-white hover:bg-blue-600"
          }`}
        >
          Prev
        </button>

        <span className="text-sm text-gray-600">
          Page {currentPage} of {totalPages}
        </span>

        <button
          onClick={handleNext}
          disabled={!hasData || currentPage >= totalPages}
          className={`px-3 py-1 rounded ${
            !hasData || currentPage >= totalPages
              ? "bg-gray-200 text-gray-500 cursor-not-allowed"
              : "bg-blue-500 text-white hover:bg-blue-600"
          }`}
        >
          Next
        </button>
      </div>
    </div>
  );
}
